import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TOOLS } from '@/constants/tools';
import {
  getRecentGleapActions,
  identifyGleapAnonymous,
  initGleap,
  softReinitializeGleap,
  setGleapCustomData,
  setGleapTags,
  trackGleapEvent,
} from './index';

const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000;

function getToolForPath(pathname: string) {
  return TOOLS.find((tool) => pathname.startsWith(tool.path));
}

export function GleapProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const lastHeartbeatRef = useRef<number>(0);

  const tool = useMemo(() => getToolForPath(location.pathname), [location.pathname]);

  useEffect(() => {
    initGleap();
    identifyGleapAnonymous();
  }, []);

  useEffect(() => {
    const now = new Date().toISOString();
    const toolId = tool?.id;
    const toolName = tool?.title;

    softReinitializeGleap();

    setGleapTags([
      `tool:${toolId || 'none'}`,
      `path:${location.pathname || '/'}`,
    ]);

    setGleapCustomData({
      path: location.pathname,
      search: location.search,
      tool_id: toolId,
      tool_name: toolName,
      last_active_at: now,
      recent_actions: getRecentGleapActions(),
    });

    trackGleapEvent('page_viewed', {
      path: location.pathname,
      search: location.search,
      tool_id: toolId,
      tool_name: toolName,
    });

    trackGleapEvent('pageView', {
      page: location.pathname,
    });
  }, [location.pathname, location.search, tool?.id, tool?.title]);

  useEffect(() => {
    const sendHeartbeat = () => {
      const now = Date.now();
      if (now - lastHeartbeatRef.current < HEARTBEAT_INTERVAL_MS) return;
      lastHeartbeatRef.current = now;

      const toolId = tool?.id;
      const toolName = tool?.title;

      setGleapCustomData({
        last_active_at: new Date().toISOString(),
        tool_id: toolId,
        tool_name: toolName,
        recent_actions: getRecentGleapActions(),
      });

      trackGleapEvent('active_user_heartbeat', {
        tool_id: toolId,
        tool_name: toolName,
        path: location.pathname,
      });
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [location.pathname, tool?.id, tool?.title]);

  return children;
}
